import { useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import Cookies from 'js-cookie';
import { BookOpenText, MessageSquareText, Power } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTE } from '@/routes/constants';
import { THEME, THEME_ORDER, type Theme } from '@/components/login-theme';
import { useThemeAttr, setThemeAttr } from '@/hooks/use-theme-attr';
import GamificationHeader, { GamificationLeaderboard } from '@/components/gamification/GamificationHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
// 主题轮盘走打包导入(带 hash)避免部署环境 public 绝对路径破图
import themeWheel from '@/assets/images/theme-wheel.svg';
// 侧栏品牌 logo(橙色图标 + 黑字「易學平台」/ 英文版；icon_before_logo 是白线,浅色侧栏不可见故弃用) + 机构 logo + 头像
import logoZh from '@/assets/images/logo.svg';
import logoEn from '@/assets/images/logo_en.svg';
import ugcEduhkLogo from '@/assets/practice/ugc-eduhk-logo.png';
import BoySvg from '@/assets/images/avatar-boy-svg.svg';
import GirlSvg from '@/assets/images/avatar-girl-svg.svg';
// 侧栏导航图标：未选中=灰描边(<img>)；选中=内联 SVG(currentColor 跟随 --sidebar-active)
import overviewIcon from '@/assets/nav/overview.svg';
import overviewIconActive from '@/assets/nav/overview-active.svg?raw';
import practiceIcon from '@/assets/nav/practice.svg';
import practiceIconActive from '@/assets/nav/practice-active.svg?raw';
import partnerIcon from '@/assets/nav/partner.svg';
import partnerIconActive from '@/assets/nav/partner-active.svg?raw';

// 手机横屏(844×390)学生 dashboard 共享外壳:侧栏+顶栏控制+积分卡为各页固定 chrome,主体由 children 传入;整块 FitScale 等比缩放居中,舞台内用固定 px 内联样式(勿用响应式 class,否则按真实视口求值破坏定位)

const STG_W = 844;
const STG_H = 390;

// 競賽規則 PDF(取 assets/document 下首个 PDF)；相对路径(非 @/ 别名)以免 glob 生产 build 报错
const rulesPdfModules = import.meta.glob('../assets/document/*.pdf', { eager: true, query: '?url', import: 'default' });
const rulesPdfUrl = Object.values(rulesPdfModules)[0] as string | undefined;

// 宽屏铺满(灌入中部):视口比 844:390 更宽时按高度缩放,横向余量作为 shift 交舞台内元素重新落位(侧栏贴左/顶栏+积分卡贴右/主体吃 shift 变宽)消留白;更窄/高时等比居中(shift=0)。同登录画布 shift 技术,各页主体经 useStageFit() 取同一 shift
export function useStageFit() {
  const [fit, setFit] = useState({ scale: 1, shift: 0 });
  useEffect(() => {
    const update = () => {
      const sW = window.innerWidth / STG_W;
      const sH = window.innerHeight / STG_H;
      if (sW >= sH) setFit({ scale: sH, shift: window.innerWidth / sH - STG_W });
      else setFit({ scale: sW, shift: 0 });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return fit;
}

interface Props {
  children: ReactNode;
}

export default function MobileLandscapeDashboardShell({ children }: Props) {
  const { t, i18n } = useTranslation();
  const { getCurrentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = getCurrentUser();
  const theme = useThemeAttr();

  // 宽屏铺满:按高度缩放并把横向余量 shift 灌入舞台(见 useStageFit)。侧栏贴左 / 顶栏+积分卡贴右 / 页面主体吃 shift。
  const { scale, shift } = useStageFit();

  // 语言切换(与登录横屏一致)
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const isEn = current.startsWith('en');
  const setLang = (lng: 'en' | 'zh-HK') => { i18n.changeLanguage(lng); Cookies.set('i18next', lng); };

  // Sentry 意见反馈表单(仅生产初始化;开发无操作) —— 与 app-sidebar 一致
  const openFeedback = async () => {
    try {
      const feedback = Sentry.getFeedback?.();
      if (!feedback) return;
      const form = await feedback.createForm();
      form.appendToDom();
      form.open();
    } catch (e) {
      console.warn('Feedback widget unavailable:', e);
    }
  };

  // 主题驱动的胶囊配色：橙色主题用设计稿原色,其余主题跟随 var(--accent)(与桌面 chrome 同套路)
  const rulesBg = theme === 'orange' ? '#8FCEE0' : 'var(--accent)';
  const boardBg = theme === 'orange' ? '#FFD6DC' : 'var(--accent)';
  const langBg = theme === 'orange' ? '#FFE7D0' : 'var(--accent)';

  // 主题切换器色块(与 ThemeSelector 一致：橙=四色轮盘,其余=纯色圆点)
  const swatch = (th: Theme, size: number) =>
    th === 'orange'
      ? <img src={themeWheel} alt="" style={{ width: size, height: size, display: 'block' }} />
      : <span style={{ width: size, height: size, borderRadius: '50%', background: THEME[th].dot, display: 'block' }} />;
  const others = THEME_ORDER.filter((x) => x !== theme);

  const NAV = [
    { url: ROUTE.STUDENT.OVERVIEW, title: t('student.overview'), icon: overviewIcon, active: overviewIconActive, top: 140, related: [] as string[] },
    { url: ROUTE.STUDENT.PRACTICE_HOME, title: t('student.practice'), icon: practiceIcon, active: practiceIconActive, top: 183, related: [ROUTE.STUDENT.PRACTICE_EDIT] },
    { url: ROUTE.STUDENT.SELF_ASSESS, title: t('student.aibot'), icon: partnerIcon, active: partnerIconActive, top: 229, related: [] as string[] },
  ];

  const avatarSrc = user?.sex === 'female' ? GirlSvg : user?.sex === 'X' ? null : BoySvg;

  return (
    <div
      style={{ width: STG_W + shift, height: STG_H, transform: `scale(${scale})`, transformOrigin: 'center', position: 'relative', flex: '0 0 auto' }}
    >
      {/* ─── 侧边栏 ─── */}
      <div style={{ position: 'absolute', left: 11, top: 12, width: 182, height: 366, borderRadius: 20, background: '#F3F3F4' }}>
        {/* 品牌 logo(橙色图标 + 易學平台/英文版)：frame 组 [63,26 79x24] → 容器内 rel(52,14) */}
        <img src={isEn ? logoEn : logoZh} alt="易學平台" draggable={false} style={{ position: 'absolute', left: 52, top: 14, height: 24, width: 'auto', display: 'block' }} />
        {/* 头像(圆形) frame [84,65] → rel(73,53) */}
        <div style={{ position: 'absolute', left: 73, top: 53, width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', background: '#000' }}>
          {avatarSrc && <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
        {/* 姓名 / 邮箱(整栏居中) frame 107/125 → rel 95/113 */}
        <div style={{ position: 'absolute', left: 0, top: 95, width: 182, textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#000', fontFamily: 'var(--font-sans)' }}>
          {user?.name}
        </div>
        <div style={{ position: 'absolute', left: 0, top: 113, width: 182, textAlign: 'center', fontSize: 11, color: 'rgba(115,119,123,0.7)', fontFamily: 'var(--font-sans)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 8px' }}>
          {user?.email}
        </div>

        {/* 导航(3 项) */}
        {NAV.map((n) => {
          const isActive = pathname === n.url || n.related.includes(pathname);
          return (
            <button
              key={n.url}
              type="button"
              onClick={() => navigate(n.url)}
              style={{
                position: 'absolute', left: 16, top: n.top, width: 153, height: 36, borderRadius: 11,
                background: isActive ? '#FFFFFF' : 'transparent',
                boxShadow: isActive ? '0 2px 6px -3px rgba(0,0,0,0.25)' : 'none',
                border: 0, cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', gap: 11, paddingLeft: 15,
                color: isActive ? 'var(--sidebar-active)' : '#64748B',
              }}
              className={isActive ? '' : 'hover:bg-white/60'}
            >
              {isActive
                ? <span aria-hidden style={{ width: 15, height: 15, display: 'inline-flex', flexShrink: 0 }} className="[&>svg]:size-full" dangerouslySetInnerHTML={{ __html: n.active }} />
                : <img src={n.icon} alt="" style={{ width: 15, height: 15, flexShrink: 0, display: 'block' }} />}
              <span style={{ fontSize: 12, fontWeight: isActive ? 500 : 400, fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>{n.title}</span>
            </button>
          );
        })}

        {/* 机构 logo(UGC + 教大)：整栏居中(144 宽 → left 19),置于 Feedback 上方 */}
        <img src={ugcEduhkLogo} alt="" style={{ position: 'absolute', left: 19, top: 294, width: 144, height: 'auto', display: 'block' }} />

        {/* Feedback 按钮 */}
        <button
          type="button"
          onClick={openFeedback}
          style={{
            position: 'absolute', left: 16, top: 318, width: 153, height: 36, borderRadius: 11,
            background: 'var(--practice-feedback-bg)', color: 'var(--practice-feedback-fg)',
            border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 11, paddingLeft: 15,
          }}
          className="hover:brightness-95"
        >
          <MessageSquareText size={13} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>{t('common.feedback')}</span>
        </button>
      </div>

      {/* ─── 顶栏控制行(y=12 h=24) ─── */}
      {/* 主题切换器 */}
      <div style={{ position: 'absolute', left: 361 + shift, top: 12, height: 24, display: 'flex', alignItems: 'center', gap: 7, padding: '0 7px', borderRadius: 12, background: '#FFFFFF' }}>
        <span style={{ lineHeight: 0 }}>{swatch(theme, 15)}</span>
        <span style={{ width: 1, height: 15, background: '#F3F3F4' }} />
        {others.map((th) => (
          <button key={th} type="button" onClick={() => setThemeAttr(th)} aria-label={`switch to ${th} theme`} className="transition-transform hover:scale-110" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
            {swatch(th, 15)}
          </button>
        ))}
      </div>

      {/* 規則(打开競賽規則 PDF 弹窗) */}
      <Dialog>
        <DialogTrigger asChild>
          <button type="button" style={{ position: 'absolute', left: 499 + shift, top: 12, width: 67, height: 24, borderRadius: 12, background: rulesBg, border: 0, cursor: 'pointer', fontSize: 12, color: '#000', fontFamily: 'var(--font-sans)' }} className="hover:brightness-[0.97]">
            {t('common.rules', '規則')}
          </button>
        </DialogTrigger>
        {rulesPdfUrl && (
          /* 手机横屏(高≤500)：按设计稿 400×514.69 等比缩到贴合 390 舞台,尽量放大 → 292×374
             (高取 min 防更矮视口溢出,仅留极小边距)。showTitle={false} 去掉 ui/dialog 默认注入的
             空标题(否则顶部凭空多一截,标题被顶下去);收紧上/内边距与间距让标题贴顶、PDF 卡片更大。
             仅 max-height:500px 生效,不动电脑/平板(此壳本就横屏专用)。 */
          <DialogContent
            showTitle={false}
            className="sm:max-w-3xl h-[85vh] flex flex-col [@media(max-height:500px)]:!w-[292px] [@media(max-height:500px)]:!max-w-[292px] [@media(max-height:500px)]:!h-[min(374px,calc(100vh-1rem))] [@media(max-height:500px)]:!px-4 [@media(max-height:500px)]:!pt-4 [@media(max-height:500px)]:!pb-4 [@media(max-height:500px)]:!gap-2.5"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
                <BookOpenText className="h-5 w-5 text-primary" />
                {t('user.competition-rules-title', '競賽規則')}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-auto rounded-lg border">
              <iframe src={rulesPdfUrl} className="w-full h-full" title={t('common.rules', '規則')} />
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* 排行榜(复用 GamificationLeaderboard 的弹窗+数据,自定义胶囊触发器) */}
      <GamificationLeaderboard>
        <button type="button" style={{ position: 'absolute', left: 575 + shift, top: 12, width: 67, height: 24, borderRadius: 12, background: boardBg, border: 0, cursor: 'pointer', fontSize: 12, color: '#000', fontFamily: 'var(--font-sans)' }} className="hover:brightness-[0.97]">
          {t('common.leaderboard')}
        </button>
      </GamificationLeaderboard>

      {/* 语言切换 */}
      <div style={{ position: 'absolute', left: 651 + shift, top: 12, width: 109, height: 24, display: 'flex', alignItems: 'center', gap: 4, padding: 2, borderRadius: 12, background: langBg }}>
        <button type="button" onClick={() => setLang('en')} style={{ flex: '1 1 0', height: 20, borderRadius: 10, background: isEn ? '#FFF' : 'transparent', color: '#000', fontSize: 12, fontWeight: isEn ? 500 : 400, border: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>English</button>
        <button type="button" onClick={() => setLang('zh-HK')} style={{ flex: '1 1 0', height: 20, borderRadius: 10, background: !isEn ? '#FFF' : 'transparent', color: '#000', fontSize: 12, fontWeight: !isEn ? 500 : 400, border: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>繁體</button>
      </div>

      {/* 登出 */}
      <button type="button" onClick={logout} style={{ position: 'absolute', left: 769 + shift, top: 12, width: 64, height: 24, borderRadius: 12, background: '#F3F3F4', border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: '#8F8F8F', fontSize: 12, fontFamily: 'var(--font-sans)' }} className="hover:brightness-95">
        <Power size={12} />
        {t('common.logout')}
      </button>

      {/* 右上积分卡(复用 GamificationHeader，含 MPL/KP/GS)：卡已原生 216×43(Figma 569-930)，右上角锚定 rel(617,46)，不再二次缩放 */}
      <div style={{ position: 'absolute', top: 46, right: 11 }}>
        <GamificationHeader />
      </div>

      {/* ─── 页面主体(标题 + 面板)：各页在舞台坐标系内 absolute 定位 ─── */}
      {children}
    </div>
  );
}
